import type { z } from "zod";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface RequestOptions<T> extends RequestInit {
  schema?: z.ZodType<T>;
}

async function request<T>(
  path: string,
  opts: RequestOptions<T> = {},
): Promise<T> {
  // Only claim JSON when we're actually sending a JSON body. Setting the
  // Content-Type on a bodyless request makes Fastify reject it as malformed.
  const hasJsonBody = typeof opts.body === "string";

  const res = await fetch(path, {
    ...opts,
    // Session cookie travels with every request; bearer-token auth is only
    // for non-browser callers (CLI, CI) which don't go through this client.
    credentials: "include",
    headers: {
      ...(hasJsonBody ? { "Content-Type": "application/json" } : {}),
      ...(opts.headers ?? {}),
    },
  });

  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => undefined);
    }
    throw new ApiError(res.status, `${res.status} ${res.statusText}`, body);
  }

  const data = await res.json();
  return opts.schema ? opts.schema.parse(data) : (data as T);
}

export const apiClient = {
  get: <T>(path: string, schema?: z.ZodType<T>) =>
    request<T>(path, { method: "GET", schema }),
  post: <T>(path: string, body?: unknown, schema?: z.ZodType<T>) =>
    request<T>(path, {
      method: "POST",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      schema,
    }),
  put: <T>(path: string, body?: unknown, schema?: z.ZodType<T>) =>
    request<T>(path, {
      method: "PUT",
      body: JSON.stringify(body),
      schema,
    }),
  patch: <T>(path: string, body?: unknown, schema?: z.ZodType<T>) =>
    request<T>(path, {
      method: "PATCH",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      schema,
    }),
  delete: <T>(path: string, schema?: z.ZodType<T>) =>
    request<T>(path, { method: "DELETE", schema }),
  upload: async <T>(
    path: string,
    file: File,
    schema?: z.ZodType<T>,
  ): Promise<T> => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(path, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
    if (!res.ok) {
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        body = await res.text().catch(() => undefined);
      }
      throw new ApiError(res.status, `${res.status} ${res.statusText}`, body);
    }
    const data = await res.json();
    return schema ? schema.parse(data) : (data as T);
  },
};
